import type {
  BaseItem,
  Content,
  AlignmentPoints,
  SnapState,
  AlignmentVisibility,
} from '@messenger/types'
import { useState, useRef, useCallback, useEffect } from 'react'
import {
  applyDragConstraints,
  applyReleaseSnap,
  SNAP_CATCH_UP_DURATION_MS,
  SNAP_FREEZE_DURATION_MS,
} from '@/utils/editor/applyDragConstraints'
import { calculateAlignmentSnap } from '@/utils/editor/calculateAlignmentSnap'

type Padding = Content['product']['padding']

type DragElement = {
  x: number
  y: number
  deltaX: number
  deltaY: number
}

type UseSnapToGuidesParams = {
  item: Pick<BaseItem, 'width' | 'height'>
  padding: Padding
  alignmentPoints: AlignmentPoints
  snapToggle: boolean
  controlledX: number
  controlledY: number
  setControlledX: (x: number) => void
  setControlledY: (y: number) => void
  setAlignmentVisibility: (visibility: AlignmentVisibility) => void
}

/**
 * Custom hook to manage snap-to-guides behavior with freeze and animation
 * Encapsulates all snap state, freeze logic, and animation handling
 */
export const useSnapToGuides = ({
  item,
  padding,
  alignmentPoints,
  snapToggle,
  controlledX,
  controlledY,
  setControlledX,
  setControlledY,
  setAlignmentVisibility,
}: UseSnapToGuidesParams) => {
  // Track snap state for each axis
  const snapStateRef = useRef<{
    vertical: SnapState
    horizontal: SnapState
  }>({
    vertical: {
      guidePosition: null,
      hasSnapped: false,
      snapTimestamp: 0,
      hasCompletedSnap: false,
    },
    horizontal: {
      guidePosition: null,
      hasSnapped: false,
      snapTimestamp: 0,
      hasCompletedSnap: false,
    },
  })

  // Track cursor position during freeze to animate to after freeze ends
  const targetPositionRef = useRef({ x: 0, y: 0 })

  // Track animation state
  const animationRef = useRef<number | null>(null)
  const [isAnimatingToTarget, setIsAnimatingToTarget] = useState(false)

  // Animate item smoothly from current position to target cursor position
  const startAnimationToCursor = useCallback(() => {
    // Cancel any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
    }

    const startX = controlledX
    const startY = controlledY
    const targetX = targetPositionRef.current.x
    const targetY = targetPositionRef.current.y
    const startTime = Date.now()

    setIsAnimatingToTarget(true)

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / SNAP_CATCH_UP_DURATION_MS, 1)

      // Ease-out curve for smooth deceleration
      const easeProgress = 1 - Math.pow(1 - progress, 3)

      // Interpolate position
      const currentX = startX + (targetX - startX) * easeProgress
      const currentY = startY + (targetY - startY) * easeProgress

      setControlledX(currentX)
      setControlledY(currentY)

      if (progress < 1) {
        // Continue animation
        animationRef.current = requestAnimationFrame(animate)
      } else {
        // Animation complete - mark snap as completed and clear timestamps
        // This allows free dragging on subsequent drags without freeze
        snapStateRef.current.vertical.snapTimestamp = 0
        snapStateRef.current.vertical.hasCompletedSnap = true
        snapStateRef.current.horizontal.snapTimestamp = 0
        snapStateRef.current.horizontal.hasCompletedSnap = true
        setIsAnimatingToTarget(false)
        animationRef.current = null
      }
    }

    animationRef.current = requestAnimationFrame(animate)
  }, [controlledX, controlledY, setControlledX, setControlledY])

  // Handle drag with snap constraints
  const handleSnapDrag = useCallback(
    (dragElement: DragElement) => {
      // Only apply freeze if this is the INITIAL snap (hasCompletedSnap = false)
      // After user has been snapped once and releases, they can drag freely
      const hasCompletedVerticalSnap =
        snapStateRef.current.vertical.hasCompletedSnap
      const hasCompletedHorizontalSnap =
        snapStateRef.current.horizontal.hasCompletedSnap

      // Check if item is frozen after snap (0.4 second freeze period)
      // Only freeze if we haven't completed the snap sequence yet
      const now = Date.now()
      const verticalFrozen =
        !hasCompletedVerticalSnap &&
        snapStateRef.current.vertical.snapTimestamp > 0 &&
        now - snapStateRef.current.vertical.snapTimestamp <
          SNAP_FREEZE_DURATION_MS
      const horizontalFrozen =
        !hasCompletedHorizontalSnap &&
        snapStateRef.current.horizontal.snapTimestamp > 0 &&
        now - snapStateRef.current.horizontal.snapTimestamp <
          SNAP_FREEZE_DURATION_MS

      const isFrozen = verticalFrozen || horizontalFrozen

      // If frozen, track cursor position for animation after freeze
      if (isFrozen) {
        // Store target position (where cursor wants item to be)
        targetPositionRef.current.x = dragElement.x
        targetPositionRef.current.y = dragElement.y
        // Don't allow any movement during freeze
        return false
      }

      // Check if freeze just ended and we need to start animation
      const verticalJustUnfroze =
        !hasCompletedVerticalSnap &&
        snapStateRef.current.vertical.snapTimestamp > 0 &&
        now - snapStateRef.current.vertical.snapTimestamp >=
          SNAP_FREEZE_DURATION_MS &&
        !isAnimatingToTarget
      const horizontalJustUnfroze =
        !hasCompletedHorizontalSnap &&
        snapStateRef.current.horizontal.snapTimestamp > 0 &&
        now - snapStateRef.current.horizontal.snapTimestamp >=
          SNAP_FREEZE_DURATION_MS &&
        !isAnimatingToTarget

      if (verticalJustUnfroze || horizontalJustUnfroze) {
        targetPositionRef.current.x = dragElement.x
        targetPositionRef.current.y = dragElement.y
        // Freeze just ended - start animation to cursor position
        startAnimationToCursor()
        return false
      }

      // If animating to cursor, don't allow manual dragging
      if (isAnimatingToTarget) {
        return false
      }

      // Apply snap-to-guide constraints using utility function
      const { finalX, finalY, snapInfo } = applyDragConstraints(
        dragElement,
        item,
        padding,
        alignmentPoints,
        snapToggle,
        snapStateRef,
      )

      // Set the constrained position
      setControlledX(finalX)
      setControlledY(finalY)

      // Update guide visibility
      setAlignmentVisibility(snapInfo)

      return true
    },
    [
      item,
      padding,
      alignmentPoints,
      snapToggle,
      isAnimatingToTarget,
      startAnimationToCursor,
      setControlledX,
      setControlledY,
      setAlignmentVisibility,
    ],
  )

  // Handle drag stop with snap release
  const handleSnapStop = useCallback(
    (finalX: number, finalY: number) => {
      // Cancel any ongoing animation FIRST before calculating anything
      // This prevents race conditions between animation callbacks and snap logic
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
      setIsAnimatingToTarget(false)

      // Clear the target position reference to prevent stale cursor positions
      // from interfering with the next drag operation
      targetPositionRef.current = { x: 0, y: 0 }

      // The position released is the one the item is painted at, never the
      // pointer's: while the freeze or the catch-up holds the item at a guide,
      // letting go leaves it there. Snap-on-release runs first because the
      // snapped position decides whether the item is still in a zone.
      const snappedPosition = applyReleaseSnap(
        finalX,
        finalY,
        item,
        padding,
        alignmentPoints,
        snapToggle,
      )

      // Now check if item is still in snap zone using the SNAPPED position
      // This allows re-entry to trigger snap again if the item leaves the zone
      const itemCenterX =
        (padding?.left || 0) + snappedPosition.finalX + item.width / 2
      const itemCenterY =
        (padding?.top || 0) + snappedPosition.finalY + item.height / 2
      const { verticalSnapZone, horizontalSnapZone } = calculateAlignmentSnap(
        itemCenterX,
        itemCenterY,
        alignmentPoints,
      )

      // Reset vertical snap state if exited zone or different guide
      const shouldResetVertical =
        !verticalSnapZone ||
        (verticalSnapZone &&
          verticalSnapZone.guidePosition !==
            snapStateRef.current.vertical.guidePosition)
      if (shouldResetVertical) {
        snapStateRef.current.vertical = {
          guidePosition: null,
          hasSnapped: false,
          snapTimestamp: 0,
          hasCompletedSnap: false,
        }
      } else {
        // Still in same zone - clear timestamps but mark as completed
        // This prevents re-freeze on the next drag of the same item
        snapStateRef.current.vertical.snapTimestamp = 0
        snapStateRef.current.vertical.hasCompletedSnap = true
      }

      // Reset horizontal snap state if exited zone or different guide
      const shouldResetHorizontal =
        !horizontalSnapZone ||
        (horizontalSnapZone &&
          horizontalSnapZone.guidePosition !==
            snapStateRef.current.horizontal.guidePosition)
      if (shouldResetHorizontal) {
        snapStateRef.current.horizontal = {
          guidePosition: null,
          hasSnapped: false,
          snapTimestamp: 0,
          hasCompletedSnap: false,
        }
      } else {
        // Still in same zone - clear timestamps but mark as completed
        // This prevents re-freeze on the next drag of the same item
        snapStateRef.current.horizontal.snapTimestamp = 0
        snapStateRef.current.horizontal.hasCompletedSnap = true
      }

      return snappedPosition
    },
    [item, padding, alignmentPoints, snapToggle],
  )

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  return {
    handleSnapDrag,
    handleSnapStop,
    isAnimatingToTarget,
  }
}
